import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["messages", "input", "submitButton", "documentSelect", "sidebar", "backdrop", "tab", "openIcon", "closeIcon"]
  static values = {
    url: String,
    conversationId: { type: String, default: null },
    documentId: { type: String, default: null }
  }

  connect() {
    // Set initial document selection from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const documentId = urlParams.get('document_id');
    const conversationId = urlParams.get('conversation_id');
    
    // Set conversation ID if present in URL
    if (conversationId) {
      this.conversationIdValue = conversationId;
      // console.log('Setting conversation ID from URL:', conversationId);
    }
    
    if (documentId) {
      this.documentSelectTarget.value = documentId;
      this.documentIdValue = documentId;
      // Store the original document ID to maintain context
      this.originalDocumentId = documentId;
    } else {
      // If no document_id in URL, we're in All Documents mode
      this.documentIdValue = 'all';
      this.originalDocumentId = 'all';
    }
    
    // console.log('Initial state:', {
    //   documentId: this.documentIdValue,
    //   originalDocumentId: this.originalDocumentId,
    //   conversationId: this.conversationIdValue
    // });

    this.loadMessages();

    // Add click handlers for view translation buttons
    document.querySelectorAll('[data-action="view-translation"]').forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.showSidebar(event);
        this.onButtonClick(event);
      });
    });
  }

  onButtonClick(event) {
    const button = event.currentTarget;
    const documentId = button.getAttribute('data-document-id');
  
    // Find the corresponding option in the select list and select it
    const selectElement = this.documentSelectTarget;
    if (selectElement) {
      const option = selectElement.querySelector(`option[value="${documentId}"]`);
      if (option) {
        selectElement.value = documentId;
        // Trigger the change event to update any related UI or logic
        selectElement.dispatchEvent(new Event('change'));
      }
    }
  }

  clearTranslations() {
    const resultContainer = document.getElementById('translationResult');
    const originalText = document.getElementById('originalText');
    const translatedText = document.getElementById('translatedText');
    
    if (resultContainer) {
      resultContainer.classList.add('d-none');
    }
    if (originalText) originalText.innerHTML = '';
    if (translatedText) translatedText.innerHTML = '';
  }

  onDocumentSelect(event) {
    const selectedId = event.target.value;
    this.documentIdValue = selectedId; // Keep 'all' as the value for All Documents
    this.originalDocumentId = selectedId; // Update the original document ID when manually changing
    
    // Only reset conversation ID when switching to a specific document
    // For All Documents, we want the server to find the existing conversation
    if (selectedId !== 'all') {
      this.conversationIdValue = null;
    } else {
      // For All Documents, set to null to let server find the right conversation
      this.conversationIdValue = null;
    }
    
    // console.log('Document changed:', {
    //   newDocumentId: selectedId,
    //   conversationId: this.conversationIdValue
    // });
  
    // Pass through the source from the original event
    this.element.dispatchEvent(new CustomEvent('chat:documentSelected', {
      bubbles: true,
      detail: { 
        documentId: selectedId === 'all' ? null : selectedId,
        source: event.detail?.source || 'select'
      }
    }));

    // Hide translation result and clear content when selecting "All Documents"
    if (selectedId === 'all') {
      this.clearTranslations();
    } else {
      // Load translation if a specific document is selected
      fetch(`/pdf_translator/get_translation/${selectedId}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      .then(response => response.json())
      .then(data => {
        const resultContainer = document.getElementById('translationResult');
        const originalText = document.getElementById('originalText');
        const translatedText = document.getElementById('translatedText');
        
        if (resultContainer && originalText && translatedText) {
          resultContainer.classList.remove('d-none');
          originalText.innerHTML = this.formatParagraphs(data.original_text || '');
          translatedText.innerHTML = this.formatParagraphs(data.translated_text || '');
          resultContainer.scrollIntoView({ behavior: 'smooth' });
        }
      })
      .catch(error => console.error('Error:', error));
    }
  
    // Always load messages
    this.loadMessages();
  }

  formatParagraphs(text) {
    return text.split(/\n\s*\n/).map((para, i) => 
      `<div class="paragraph" data-para-id="${i}">${para.trim().replace(/\n/g, '<br>')}</div>`
    ).join('\n\n')
  }

  onKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.submit(event)
    }
  }

  submit(event) {
    event.preventDefault()
    const query = this.inputTarget.value.trim()
    if (!query) return

    this.submitButtonTarget.disabled = true
    this.addMessage('user', query)
    this.inputTarget.value = ''
    
    // Use the original document context from when the chat was loaded
    // This ensures we stay in the same chat mode throughout the conversation
    const originalDocumentId = this.originalDocumentId || this.documentIdValue;
    const isAllDocuments = !originalDocumentId || originalDocumentId === 'null' || originalDocumentId === 'all';
    
    // Set the current document ID based on the original context
    if (isAllDocuments) {
      this.documentIdValue = 'all';
    } else {
      this.documentIdValue = originalDocumentId;
    }
    
    const params = {
      query: query
    }
    
    // Always include conversation_id if we have one to maintain chat history
    if (this.conversationIdValue && this.conversationIdValue !== 'null') {
      params.conversation_id = this.conversationIdValue
    }
    
    // Only include document_id if we're not in All Documents mode
    if (!isAllDocuments) {
      params.document_id = originalDocumentId
    }
    
    // console.log('Submitting chat with params:', params);
    
    // Update the URL without reloading the page, but only if we're already in a document-specific chat
    if (!isAllDocuments) {
      const url = new URL(window.location.href);
      const currentUrlDocId = url.searchParams.get('document_id');
      // Only update if we're already in a document-specific chat
      if (currentUrlDocId && currentUrlDocId !== 'all') {
        url.searchParams.set('document_id', originalDocumentId);
        window.history.pushState({}, '', url);
      }
    }
    
    fetch(this.urlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(params)
    })
    .then(response => {
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError("Response was not JSON")
      }
      return response.json()
    })
    .then(data => {
      // Ensure we maintain the same document context after response
      // Use the original document context to ensure consistency
      if (isAllDocuments) {
        this.documentIdValue = 'all';
      } else {
        this.documentIdValue = originalDocumentId;
      }
      
      // Store the conversation ID from the response to maintain chat history
      if (data && data.conversation_id) {
        this.conversationIdValue = data.conversation_id;
        // console.log('Updated conversation ID:', this.conversationIdValue);
      }
      
      this.loadMessages()
      this.submitButtonTarget.disabled = false
    })
    .catch(error => {
      console.error('Error:', error)
      this.submitButtonTarget.disabled = false
    })
  }

  loadMessages() {
    // Use the original document context from when the chat was loaded
    // This ensures we stay in the same chat mode throughout the conversation
    const originalDocumentId = this.originalDocumentId || this.documentIdValue;
    const isAllDocuments = !originalDocumentId || originalDocumentId === 'null' || originalDocumentId === 'all';
    
    // Set the current document ID based on the original context
    if (isAllDocuments) {
      this.documentIdValue = 'all';
    } else {
      this.documentIdValue = originalDocumentId;
    }
    
    const params = new URLSearchParams()
    
    // For All Documents chat, we want to handle it exactly like when the page loads
    // This matches the behavior in pdf_translator_controller.rb#chat
    if (this.conversationIdValue === 'null' || this.conversationIdValue === null) {
      // Don't send a conversation_id, which forces the server to find or create one
      // console.log('Not sending conversation_id to let server find the right one');
    } else if (this.conversationIdValue) {
      // For specific conversations, include the ID
      params.append('conversation_id', this.conversationIdValue)
    }
    
    // Only include document_id if we're not in All Documents mode
    if (!isAllDocuments && originalDocumentId) {
      params.append('document_id', originalDocumentId)
    }
    
    // console.log('Loading messages with params:', {
    //   conversationId: this.conversationIdValue,
    //   documentId: isAllDocuments ? 'all' : originalDocumentId,
    //   originalDocumentId: this.originalDocumentId,
    //   url: `${this.urlValue}.json?${params.toString()}`
    // });
    
    // Make sure the URL reflects the original document context and conversation ID
    const url = new URL(window.location.href);
    
    if (!isAllDocuments) {
      // For document-specific chat, ensure the URL has the correct document_id
      if (url.searchParams.get('document_id') !== originalDocumentId) {
        url.searchParams.set('document_id', originalDocumentId);
      }
    } else {
      // For All Documents mode, make sure we don't have a document_id in the URL
      if (url.searchParams.has('document_id')) {
        url.searchParams.delete('document_id');
      }
    }
    
    // Always include the conversation ID in the URL if we have one
    if (this.conversationIdValue && this.conversationIdValue !== 'null') {
      if (url.searchParams.get('conversation_id') !== this.conversationIdValue) {
        url.searchParams.set('conversation_id', this.conversationIdValue);
      }
    }
    
    // Update the URL
    window.history.pushState({}, '', url);
    
    fetch(`${this.urlValue}.json?${params.toString()}`, {
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => {
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError("Response was not JSON")
      }
      return response.json()
    })
    .then(data => {
      // console.log('Received messages:', data)
      if (data.messages) {
        // console.log('First message translation_history:', data.messages[0]?.translation_history)
        // console.log('documentIdValue before rendering:', this.documentIdValue);
        
        // If we received a conversation ID in the response, store it
        if (data.conversation_id) {
          this.conversationIdValue = data.conversation_id;
          // console.log('Updated conversation ID from messages response:', this.conversationIdValue);
        }
        
        // Ensure we maintain the original document context
        // This ensures we stay in the same chat mode throughout the conversation
        if (isAllDocuments) {
          this.documentIdValue = 'all';
          // Make sure the select shows 'all'
          if (this.documentSelectTarget) {
            this.documentSelectTarget.value = 'all';
          }
        } else {
          this.documentIdValue = originalDocumentId;
          // Update the document select dropdown if it exists
          if (this.documentSelectTarget && originalDocumentId) {
            this.documentSelectTarget.value = originalDocumentId;
          }
        }
        
        // Render messages
        this.messagesTarget.innerHTML = data.messages.map(message => this.messageTemplate(message)).join('')
        
        // Debug: Check if View Translation links were added
        const viewLinks = this.messagesTarget.querySelectorAll('.view-translation-link');
        // console.log(`Found ${viewLinks.length} View Translation links`);
        
        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
      }
    })
    .catch(error => {
      console.error('Error loading messages:', error)
    })
  }

  addMessage(role, content) {
    // Get the user's name from the DOM
    const userName = document.querySelector('meta[name="user-name"]')?.content || 'You';
    
    const message = {
      role: role,
      content: content,
      created_at_ago: 'just now',
      user_email: userName // Keep the property name as user_email for compatibility
    }
    this.messagesTarget.insertAdjacentHTML('beforeend', this.messageTemplate(message))
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  messageTemplate(message) {
    // console.log('Message:', message, 'documentIdValue:', this.documentIdValue);
    // Only show view link for assistant messages with translation history
    // when we're in the All Documents mode
    const isAllDocuments = !this.documentIdValue || 
                          this.documentIdValue === 'null' || 
                          this.documentIdValue === 'all';
    const showViewLink = message.role === 'assistant' && 
                         message.translation_history && 
                         message.translation_history.id && 
                         isAllDocuments;
    
    // Process the content to ensure long words are wrapped
    const processedContent = message.content.replace(/\b\S{30,}\b/g, function(match) {
      return `<span style="word-break: break-all;">${match}</span>`;
    });
    
    return `
      <div class="chat-message mb-3 ${message.role}">
        <div class="message-content" style="overflow-wrap: break-word; word-break: break-word; max-width: 100%;">
          ${processedContent}
        </div>
        <div class="message-meta text-muted small">
          <span class="sender-name">
            ${message.role === 'assistant' ? 'Assistant' : message.user_email}
          </span> •
          ${message.created_at_ago} ago
          ${showViewLink ? `
            • <a href="#" class="view-translation-link" 
                data-document-id="${message.translation_history.id}" 
                data-action="click->chat#viewTranslation">
                View Translation
              </a>
          ` : ''}
        </div>
      </div>
    `
  }

  toggleSidebar(event) {
    if (event) event.preventDefault();
    // console.log('Toggling sidebar');
    this.sidebarTarget.classList.toggle('is-visible');
    this.backdropTarget.classList.toggle('is-visible');
    this.tabTarget.classList.toggle('is-visible');
  }

  showSidebar(event) {
    if (event) event.preventDefault();
    // console.log('Showing sidebar');
    this.sidebarTarget.classList.add('is-visible');
    this.backdropTarget.classList.add('is-visible');
    this.tabTarget.classList.add('is-visible');
    
    // If this was triggered by a view translation button, update the document selection
    if (event && event.currentTarget.dataset.documentId) {
      const documentId = event.currentTarget.dataset.documentId;
      this.documentSelectTarget.value = documentId;
      this.documentIdValue = documentId;
      this.loadMessages();
    }
  }

  hideSidebar(event) {
    if (event) event.preventDefault();
    // console.log('Hiding sidebar');
    this.sidebarTarget.classList.remove('is-visible');
    this.backdropTarget.classList.remove('is-visible');
    this.tabTarget.classList.remove('is-visible');
  }

  viewTranslation(event) {
    event.preventDefault()
    const link = event.currentTarget
    const documentId = link.dataset.documentId
    
    if (documentId) {
      // Select the document in the dropdown
      const selectElement = this.documentSelectTarget;
      if (selectElement) {
        const option = selectElement.querySelector(`option[value="${documentId}"]`);
        if (option) {
          selectElement.value = documentId;
          this.documentIdValue = documentId;
          
          // Don't trigger change event yet to avoid double-loading the translation
        }
      }
      
      // Fetch and display translation directly
      fetch(`/pdf_translator/get_translation/${documentId}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      .then(response => response.json())
      .then(data => {
        const resultContainer = document.getElementById('translationResult')
        const originalText = document.getElementById('originalText')
        const translatedText = document.getElementById('translatedText')
        
        if (resultContainer && originalText && translatedText) {
          resultContainer.classList.remove('d-none')
          originalText.innerHTML = this.formatParagraphs(data.original_text || '')
          translatedText.innerHTML = this.formatParagraphs(data.translated_text || '')
          resultContainer.scrollIntoView({ behavior: 'smooth' })
          
          // Now trigger change event to update the chat messages
          if (selectElement) {
            selectElement.dispatchEvent(new Event('change'));
          }
        }
      })
      .catch(error => console.error('Error:', error))
    }
  }
}
